import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRight } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [projectsRef, projectsInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [blogRef, blogInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [featuredProjects, setFeaturedProjects] = useState([]);

  useEffect(() => {
    fetchFeaturedProjects();
  }, []);

  const fetchFeaturedProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      // Tomar los primeros 3 proyectos
      setFeaturedProjects(response.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  return (
    <>
      <SEO />
      
      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center relative grain-texture" data-testid="hero-section">
        <motion.div
          ref={heroRef}
          initial={{ opacity: 0 }}
          animate={heroInView ? { opacity: 1 } : {}}
          transition={{ duration: 1 }}
          className="absolute inset-0 z-0"
        >
          <img
            src="https://images.unsplash.com/photo-1704751072951-a1df7122b01d?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Hero background"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-ink/40" />
        </motion.div>

        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <motion.h1
            initial={{ y: 30, opacity: 0 }}
            animate={heroInView ? { y: 0, opacity: 1 } : {}}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-6xl sm:text-7xl lg:text-9xl font-bold tracking-tighter leading-[0.8] text-paper mb-6"
            data-testid="hero-title"
          >
            DISEÑO QUE
            <br />
            <span className="text-editorial">CUENTA HISTORIAS</span>
          </motion.h1>

          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={heroInView ? { y: 0, opacity: 1 } : {}}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="text-xl md:text-2xl font-light text-paper/90 mb-12"
            data-testid="hero-subtitle"
          >
            Diseñador Gráfico & Fotógrafo
          </motion.p>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={heroInView ? { y: 0, opacity: 1 } : {}}
            transition={{ delay: 0.9, duration: 0.8 }}
          >
            <Link
              to="/projects"
              data-testid="hero-cta-button"
              className="inline-flex items-center gap-2 bg-paper text-ink px-8 py-4 text-sm tracking-widest font-sans uppercase hover:bg-editorial hover:text-paper transition-colors duration-200"
            >
              Ver Proyectos
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="featured-projects-section">
        <div className="max-w-screen-2xl mx-auto">
          <motion.div
            ref={projectsRef}
            initial={{ opacity: 0, y: 30 }}
            animate={projectsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <p className="text-sm font-sans tracking-widest uppercase text-editorial mb-4" data-testid="featured-label">
              Proyectos Destacados
            </p>
            <h2 className="text-5xl md:text-7xl font-bold tracking-tight" data-testid="featured-title">
              Trabajos Recientes
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredProjects.length > 0 ? (
              featuredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={projectsInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: index * 0.2, duration: 0.8 }}
                  className="group"
                  data-testid={`featured-project-${index + 1}`}
                >
                  <Link to={`/projects/${project.slug}`} className="block">
                    <div className="image-zoom-container editorial-border h-80 mb-4">
                      <img
                        src={project.images[0]}
                        alt={project.title}
                        className="w-full h-full object-cover image-zoom"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-xs font-sans tracking-widest uppercase text-ink/60 mb-2">
                          {project.category}
                        </p>
                        <h3 className="text-2xl font-bold tracking-tight group-hover:text-editorial transition-colors">
                          {project.title}
                        </h3>
                      </div>
                      {project.type === 'ficticio' && (
                        <span className="text-xs font-sans tracking-widest uppercase text-editorial">
                          Concepto
                        </span>
                      )}
                    </div>
                  </Link>
                </motion.div>
              ))
            ) : (
              <div className="col-span-3 text-center py-12">
                <p className="text-lg font-light text-ink/60">Cargando proyectos...</p>
              </div>
            )}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={projectsInView ? { opacity: 1 } : {}}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="mt-12 text-center"
          >
            <Link
              to="/projects"
              data-testid="view-all-projects-button"
              className="inline-flex items-center gap-2 border border-ink px-8 py-4 text-sm tracking-widest font-sans uppercase hover:bg-ink hover:text-paper transition-colors duration-200"
            >
              Ver Todos los Proyectos
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-24 md:py-32 bg-ink text-paper" data-testid="about-preview-section">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24 items-center">
            <motion.div
              ref={blogRef}
              initial={{ opacity: 0, x: -30 }}
              animate={blogInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <p className="text-sm font-sans tracking-widest uppercase text-editorial mb-4">
                Sobre Mí
              </p>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
                Creando experiencias visuales que conectan
              </h2>
              <p className="text-lg font-light leading-relaxed text-paper/80 mb-8">
                Con más de 4 años de experiencia en diseño gráfico y fotografía, me especializo en crear
                identidades visuales únicas que comunican la esencia de cada proyecto.
              </p>
              <Link
                to="/about"
                data-testid="about-link-button"
                className="inline-flex items-center gap-2 text-sm tracking-widest font-sans uppercase text-paper hover:text-editorial transition-colors group"
              >
                Conocer Más
                <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={blogInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="editorial-border"
            >
              <img
                src="https://images.unsplash.com/photo-1759247178379-0e8eba83a4a6?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="About preview"
                className="w-full h-[500px] object-cover grayscale"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 px-6" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-5xl md:text-7xl font-bold tracking-tight mb-8">
              ¿Tienes un proyecto
              <br />
              en mente?
            </h2>
            <p className="text-xl font-light leading-relaxed text-ink/70 mb-12">
              Conversemos sobre cómo puedo ayudarte a dar vida a tus ideas
            </p>
            <Link
              to="/contact"
              data-testid="cta-contact-button"
              className="inline-flex items-center gap-2 bg-ink text-paper px-12 py-5 text-sm tracking-widest font-sans uppercase hover:bg-editorial transition-colors duration-200"
            >
              Hablemos
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Home;